import * as React from 'react';
import { Input as AntInput, InputNumber, Spin, Switch, message } from 'antd';
import { BarChartOutlined, EditOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { Badge } from 'components/badge/badge';
import { Button, buttonType } from 'components/button/button';
import { Card } from 'components/card/card';
import { Input } from 'components/input/input';
import { Modal } from 'components/modal/modal';
import { Page } from 'components/page/page';
import { MarketingCampaign, MarketingCampaignType, UpdateMarketingCampaignBody } from 'core/domain';
import { getMarketingCampaigns, runUserReturnCampaign, updateMarketingCampaign } from 'core/repositories/marketing';
import { faNum, formatDate } from '../marketing-utils';
import '../marketing.scss';

const { useEffect, useState } = React;

interface EditProps {
    campaign?: MarketingCampaign;
    onClose: () => void;
    onSaved: () => void;
}

const EditCampaignModal: React.FC<EditProps> = ({ campaign, onClose, onSaved }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [form, setForm] = useState<UpdateMarketingCampaignBody>();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setIsOpen(!!campaign);
        if (campaign)
            setForm({
                id: campaign.id,
                title: campaign.title,
                description: campaign.description ?? '',
                isActive: campaign.isActive,
                delayHours: campaign.delayHours,
                notAuthenticatedMessage: campaign.notAuthenticatedMessage,
                notPurchasedMessage: campaign.notPurchasedMessage,
            });
    }, [campaign]);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        const ok = await updateMarketingCampaign(form);
        setSaving(false);
        if (!ok) return;
        message.success('کمپین ذخیره شد.');
        onClose();
        onSaved();
    };

    return (
        <Modal isOpen={isOpen} setIsOpen={setIsOpen} onClose={onClose} className="mk-modal mk-modal--wide">
            <h4>ویرایش کمپین</h4>
            {form && (
                <div className="mk-form">
                    <div className="mk-row">
                        <label className="mk-field">
                            عنوان
                            <Input
                                value={form.title}
                                placeholder="عنوان کمپین"
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                            />
                        </label>
                        <label className="mk-field">
                            ارسال چند ساعت بعد از ثبت‌نام
                            <InputNumber
                                min={1}
                                max={720}
                                value={form.delayHours}
                                onChange={(value) => setForm({ ...form, delayHours: Number(value) || 1 })}
                                style={{ width: '100%', height: 48, borderRadius: 10, paddingTop: 8 }}
                            />
                        </label>
                    </div>
                    <label className="mk-field">
                        توضیحات
                        <AntInput.TextArea
                            rows={2}
                            maxLength={1000}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </label>
                    <label className="mk-field">
                        پیامک کاربری که احراز هویت نکرده
                        <AntInput.TextArea
                            rows={5}
                            maxLength={1000}
                            showCount
                            value={form.notAuthenticatedMessage}
                            onChange={(e) => setForm({ ...form, notAuthenticatedMessage: e.target.value })}
                        />
                    </label>
                    <label className="mk-field">
                        پیامک کاربری که احراز کرده ولی خرید نکرده
                        <AntInput.TextArea
                            rows={5}
                            maxLength={1000}
                            showCount
                            value={form.notPurchasedMessage}
                            onChange={(e) => setForm({ ...form, notPurchasedMessage: e.target.value })}
                        />
                    </label>
                    <label className="mk-switch">
                        <Switch checked={form.isActive} onChange={(isActive) => setForm({ ...form, isActive })} />
                        کمپین فعال باشد
                    </label>
                </div>
            )}
            <div className="buttons">
                <Button type={buttonType.outlined} text="انصراف" onClick={onClose} />
                <Button type={buttonType.info} text="ذخیره" loading={saving} onClick={save} />
            </div>
        </Modal>
    );
};

export const MarketingCampaigns: React.FC = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<MarketingCampaign>();
    const [running, setRunning] = useState(false);

    const load = async () => setCampaigns((await getMarketingCampaigns(setLoading)) ?? []);

    useEffect(() => {
        load();
    }, []);

    const runNow = async () => {
        setRunning(true);
        const result = await runUserReturnCampaign();
        setRunning(false);
        if (!result) return;
        if (result.skipped) message.warning(result.skipReason || 'کمپین اجرا نشد.');
        else if (!result.enrolledCount) message.info('کاربر جدیدی برای این کمپین پیدا نشد.');
        else
            message.success(
                `${faNum(result.enrolledCount)} کاربر وارد کمپین شدند؛ ${faNum(result.sentCount)} پیامک ارسال شد` +
                    (result.failedCount ? `، ${faNum(result.failedCount)} ناموفق.` : '.'),
            );
        load();
    };

    return (
        <Page className="pmx-marketing" title="کمپین‌ها">
            <EditCampaignModal campaign={editing} onClose={() => setEditing(undefined)} onSaved={load} />
            <Card>
                <Spin spinning={loading}>
                    <div className="mk-campaigns">
                        {campaigns.map((campaign) => (
                            <div className="mk-campaign" key={campaign.id}>
                                <div className="mk-campaign__head">
                                    <div>
                                        <h3>{campaign.title}</h3>
                                        {campaign.description && <p>{campaign.description}</p>}
                                    </div>
                                    <Badge
                                        text={campaign.isActive ? 'فعال' : 'غیرفعال'}
                                        type={campaign.isActive ? 'success' : 'secondary'}
                                    />
                                </div>
                                <div className="mk-campaign__messages">
                                    <div>
                                        <strong>پیامک «احراز نکرده»</strong>
                                        <p>{campaign.notAuthenticatedMessage}</p>
                                    </div>
                                    <div>
                                        <strong>پیامک «خرید نکرده»</strong>
                                        <p>{campaign.notPurchasedMessage}</p>
                                    </div>
                                </div>
                                <div className="mk-campaign__footer">
                                    <div className="mk-campaign__meta">
                                        <span>
                                            ارسال: <strong>{faNum(campaign.delayHours)} ساعت بعد از ثبت‌نام</strong>
                                        </span>
                                        <span>
                                            اعضا: <strong>{faNum(campaign.membersCount)}</strong>
                                        </span>
                                        <span>
                                            پیامک ارسال‌شده: <strong>{faNum(campaign.smsSentCount)}</strong>
                                        </span>
                                        <span>
                                            آخرین اجرا: <strong>{formatDate(campaign.lastRunOn)}</strong>
                                        </span>
                                    </div>
                                    <div className="mk-campaign__actions">
                                        <Button
                                            type={buttonType.info}
                                            text="گزارش"
                                            icon={<BarChartOutlined />}
                                            onClick={() => navigate(`/dashboard/marketing/campaigns/${campaign.id}`)}
                                        />
                                        <Button
                                            type={buttonType.secondary}
                                            text="ویرایش"
                                            icon={<EditOutlined />}
                                            onClick={() => setEditing(campaign)}
                                        />
                                        {campaign.type === MarketingCampaignType.UserReturn && (
                                            <Button
                                                type={buttonType.success}
                                                text="اجرای همین حالا"
                                                icon={<PlayCircleOutlined />}
                                                loading={running}
                                                disabled={!campaign.isActive}
                                                onClick={runNow}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {!loading && !campaigns.length && <p className="mk-empty">کمپینی یافت نشد.</p>}
                    </div>
                    <p className="mk-muted">
                        کمپین‌ها خودکار هر ساعت اجرا می‌شوند. هر کاربر فقط یک بار وارد هر کمپین می‌شود.
                    </p>
                </Spin>
            </Card>
        </Page>
    );
};
